/**
 * Far & Away size drawing for the "Dimensions" accordion.
 *
 * Reads the dimension lines the store keeps in my_fields.dimensions, for example
 *   "4 x Big Plate : ∅ 26 CM / 10.1 in ; Height: 2 CM / 0.7 in"
 *   "∅: 19 CM / 7.4 in" and "H: 5 CM / 1.9 in" (one piece, named after the product)
 *   "Length: 38 CM / 15 in", "Width: 19 CM / 7.5 in", "Height: 2 CM / 0.7 in" (oval platter)
 *   "4 x Knife: Length: 23.5 cm / 9.3 in; Weight: 80 g / 2.8 oz" (cutlery)
 *   "W x H x L of the Placemats (cm): 38 x 2.5 x 46" (cork)
 * and draws every piece to one scale, each in its own card with dimension lines (arrows
 * across the top for the diameter or length, at the side for the height) and the figures
 * in both units underneath, under a title with the count ("4 × Big Plates"). Cards wrap onto
 * new rows, so nothing scrolls sideways.
 * It draws only once its accordion opens and redraws when the panel width changes.
 * When nothing can be read it stays empty and the size list next to it is shown instead.
 */

const SVG_NS = 'http://www.w3.org/2000/svg';
const CM_PER_IN = 2.54;

/**
 * @typedef {{ cm: number, inch: number }} Measure
 * @typedef {{ name: string, count?: number, across?: Measure, width?: Measure, height?: Measure, length?: Measure, kind: 'round' | 'oval' | 'cutlery' | 'flat' }} Piece
 */

/** @param {number} value */
const fmt = (value) => String(Math.round(value * 10) / 10);

/** @param {number} cm */
const fromCm = (cm) => ({ cm, inch: Math.round((cm / CM_PER_IN) * 10) / 10 });

/** Shopper's units first: imperial for the US (set per element from data-units), metric elsewhere. */
let imperialFirst = false;
/** @param {Measure} m */
const one = (m) => (imperialFirst ? `${fmt(m.inch)} in` : `${fmt(m.cm)} cm`);
/** @param {Measure} m */
const both = (m) => (imperialFirst ? `${fmt(m.inch)} in · ${fmt(m.cm)} cm` : `${fmt(m.cm)} cm · ${fmt(m.inch)} in`);
/** @param {Measure} a @param {Measure} b */
const bothPair = (a, b) =>
  imperialFirst
    ? `${fmt(a.inch)} × ${fmt(b.inch)} in · ${fmt(a.cm)} × ${fmt(b.cm)} cm`
    : `${fmt(a.cm)} × ${fmt(b.cm)} cm · ${fmt(a.inch)} × ${fmt(b.inch)} in`;

/**
 * @param {string} text
 * @param {RegExp} label
 * @returns {Measure | undefined}
 */
function measure(text, label) {
  const match = text.match(new RegExp(`${label.source}\\s*:?\\s*([\\d.]+)\\s*cm\\s*/\\s*([\\d.]+)\\s*in`, 'i'));
  if (!match) return undefined;
  const cm = parseFloat(match[1] ?? '');
  const inch = parseFloat(match[2] ?? '');
  return Number.isFinite(cm) && cm > 0 ? { cm, inch } : undefined;
}

/**
 * "Big Plate" → "Big Plates", "Knife" → "Knives"; names already plural stay as they are
 * @param {string} name
 */
export function plural(name) {
  if (/s$/i.test(name)) return name;
  if (/knife$/i.test(name)) return name.replace(/fe$/i, 'ves');
  if (/[^aeiou]y$/i.test(name)) return name.replace(/y$/i, 'ies');
  if (/(sh|ch|x)$/i.test(name)) return `${name}es`;
  return `${name}s`;
}

/** @param {string} name */
const key = (name) => name.toLowerCase().replace(/s$/, '').trim();

/**
 * The caption title: "4 × Big Plates", "1 × Serving Bowl", or just the name for a product
 * that is a single piece
 * @param {Piece} piece
 */
export function title(piece) {
  if (!piece.count) return piece.name;
  return `${piece.count} × ${piece.count > 1 ? plural(piece.name) : piece.name}`;
}

/**
 * @param {string[]} lines
 * @param {string} fallbackName
 * @param {string} [counts] - "4|Placemats;1|Serving Bowl" from the product's contents
 * @returns {Piece[]}
 */
export function parseDimensions(lines, fallbackName, counts = '') {
  /** @type {Map<string, number>} */
  const known = new Map();
  for (const pair of counts.split(';')) {
    const [count, name] = pair.split('|');
    const value = parseInt(count ?? '', 10);
    if (name && value > 0) known.set(key(name), value);
  }

  /** @type {Map<string, Piece>} */
  const pieces = new Map();

  for (const line of lines) {
    // Cork: "W x H x L of the Placemats (cm): 38 x 2.5 x 46"
    const flat = line.match(/of the\s+(.+?)\s*\(cm\)\s*:\s*([\d.]+)\s*x\s*([\d.]+)\s*x\s*([\d.]+)/i);
    if (flat) {
      const name = (flat[1] ?? '').trim();
      pieces.set(name, {
        name: name.charAt(0).toUpperCase() + name.slice(1),
        kind: 'flat',
        width: fromCm(parseFloat(flat[2] ?? '0')),
        height: fromCm(parseFloat(flat[3] ?? '0')),
        length: fromCm(parseFloat(flat[4] ?? '0')),
      });
      continue;
    }

    const named = line.match(/^\s*(\d+)\s*x\s*([^:]+?)\s*:/i);
    const name = (named?.[2] ?? fallbackName).replace(/:+$/, '').trim();
    const piece = pieces.get(name) ?? { name, kind: 'round' };
    if (named) piece.count = parseInt(named[1] ?? '', 10) || undefined;

    piece.across ??= measure(line, /∅/);
    piece.height ??= measure(line, /(?:height|\bh\b)/);
    piece.length ??= measure(line, /length/);
    piece.width ??= measure(line, /width/);
    pieces.set(name, piece);
  }

  // Pieces without a count of their own take it from the product's contents; a product made of
  // a single piece keeps none
  for (const piece of pieces.values()) {
    if (piece.count) continue;
    const count = known.get(key(piece.name)) ?? (pieces.size === 1 && known.size === 1 ? [...known.values()][0] : undefined);
    if (count && (count > 1 || pieces.size > 1)) piece.count = count;
  }

  const result = [];
  for (const piece of pieces.values()) {
    if (piece.kind === 'flat') {
      if (piece.width && piece.length) result.push(piece);
    } else if (piece.across && piece.height) {
      result.push({ ...piece, kind: 'round' });
    } else if (piece.length && piece.height) {
      result.push({ ...piece, kind: 'oval', across: piece.length });
    } else if (piece.length && !piece.height) {
      result.push({ ...piece, kind: 'cutlery' });
    }
  }
  return result;
}

/**
 * @param {string} tag
 * @param {Record<string, string | number>} [attributes]
 * @param {string} [text]
 */
function svg(tag, attributes = {}, text) {
  const element = document.createElementNS(SVG_NS, tag);
  for (const [key, value] of Object.entries(attributes)) element.setAttribute(key, String(value));
  if (text) element.textContent = text;
  return element;
}

let markerCount = 0;

/**
 * Arrowheads shared by one drawing.
 * @param {SVGSVGElement} root
 */
function addMarkers(root) {
  const id = `fa-dim-arrow-${(markerCount += 1)}`;
  const defs = svg('defs');
  const marker = svg('marker', {
    id,
    viewBox: '0 0 8 8',
    refX: '7.5',
    refY: '4',
    markerWidth: '7',
    markerHeight: '7',
    orient: 'auto-start-reverse',
    markerUnits: 'userSpaceOnUse',
  });
  marker.append(svg('path', { d: 'M0 0.8 L7.5 4 L0 7.2', class: 'fa-dim__arrow' }));
  defs.append(marker);
  root.append(defs);
  return `url(#${id})`;
}

/**
 * A dimension line with arrows (or end ticks when it is too short for arrows) and a label.
 * @param {SVGSVGElement} root
 * @param {string} arrow
 * @param {{ x1: number, y1: number, x2: number, y2: number, label: string, side: 'above' | 'right' }} options
 */
function dimension(root, arrow, { x1, y1, x2, y2, label, side }) {
  const length = Math.hypot(x2 - x1, y2 - y1);
  const vertical = x1 === x2;
  if (length >= 18) {
    root.append(svg('line', { x1, y1, x2, y2, class: 'fa-dim__line', 'marker-start': arrow, 'marker-end': arrow }));
  } else {
    // Too short for arrowheads: a line with end ticks
    root.append(svg('line', { x1, y1, x2, y2, class: 'fa-dim__line' }));
    const tick = 3.5;
    for (const [x, y] of [
      [x1, y1],
      [x2, y2],
    ]) {
      root.append(
        vertical
          ? svg('line', { x1: x - tick, y1: y, x2: x + tick, y2: y, class: 'fa-dim__line' })
          : svg('line', { x1: x, y1: y - tick, x2: x, y2: y + tick, class: 'fa-dim__line' })
      );
    }
  }
  const text =
    side === 'above'
      ? svg('text', { x: (x1 + x2) / 2, y: y1 - 6, 'text-anchor': 'middle', class: 'fa-dim__label' }, label)
      : svg('text', { x: x1 + 7, y: (y1 + y2) / 2, 'dominant-baseline': 'middle', class: 'fa-dim__label' }, label);
  root.append(text);
}

/**
 * @param {string} name
 * @param {string[]} lines
 */
function caption(name, lines) {
  const element = document.createElement('figcaption');
  const title = document.createElement('span');
  title.className = 'fa-dim__name';
  title.textContent = name;
  element.append(title);
  for (const line of lines) {
    const span = document.createElement('span');
    span.textContent = line;
    element.append(span);
  }
  return element;
}

export class FaDimensionsSketch extends HTMLElement {
  /** @type {Piece[]} */
  #pieces = [];
  #width = 0;
  /** @type {ResizeObserver | null} */
  #observer = null;

  connectedCallback() {
    imperialFirst = this.dataset.units === 'imperial';
    const template = this.querySelector('template');
    const lines = template
      ? [...template.content.querySelectorAll('li')].map((li) => li.textContent?.trim() ?? '').filter(Boolean)
      : [];
    this.#pieces = parseDimensions(lines, this.dataset.name || '', this.dataset.counts || '');
    if (!this.#pieces.length) return;

    const details = this.closest('details');
    if (details && !details.open) {
      details.addEventListener('toggle', () => details.open && this.#start());
    } else {
      this.#start();
    }
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
  }

  #start() {
    if (this.#observer) return;
    this.#observer = new ResizeObserver(() => {
      const width = Math.round(this.clientWidth);
      if (width > 0 && Math.abs(width - this.#width) > 8) this.#draw(width);
    });
    this.#observer.observe(this);
  }

  /** @param {number} width */
  #draw(width) {
    this.#width = width;
    this.querySelector('.fa-dim')?.remove();

    const cutlery = this.#pieces.filter((piece) => piece.kind === 'cutlery');
    const flat = this.#pieces.filter((piece) => piece.kind === 'flat');
    const standing = this.#pieces.filter((piece) => piece.kind === 'round' || piece.kind === 'oval');

    const wrap = document.createElement('div');
    wrap.className = 'fa-dim';
    if (standing.length) wrap.append(this.#drawStanding(standing, width));
    if (flat.length) wrap.append(this.#drawFlat(flat, width));
    if (cutlery.length) wrap.append(this.#drawCutlery(cutlery, width));
    this.append(wrap);
    this.dataset.drawn = 'ok';
  }

  /**
   * @param {number} width
   * @param {number} count
   */
  #cardWidth(width, count) {
    const gap = 16;
    const columns = Math.max(1, Math.min(count, Math.floor((width + gap) / (150 + gap))));
    return { columns, card: Math.floor((width - gap * (columns - 1)) / columns) };
  }

  /**
   * Plates, bowls, cups and platters in side view, one scale, standing on a baseline.
   * @param {Piece[]} pieces
   * @param {number} width
   */
  #drawStanding(pieces, width) {
    const { columns, card } = this.#cardWidth(width, pieces.length);
    const labelRoom = 58; // right of the piece: the height line and its label
    const widest = Math.max(...pieces.map((piece) => piece.across?.cm ?? 0));
    const tallest = Math.max(...pieces.map((piece) => piece.height?.cm ?? 0));
    const scale = Math.min((card - labelRoom - 4) / widest, 96 / tallest);
    const top = 26; // above the piece: the diameter line and its label
    const drawingHeight = Math.ceil(top + tallest * scale + 4);

    const grid = document.createElement('div');
    grid.className = 'fa-dim__grid';
    grid.style.setProperty('--fa-dim-columns', String(columns));

    for (const piece of pieces) {
      const across = /** @type {Measure} */ (piece.across);
      const height = /** @type {Measure} */ (piece.height);
      const w = across.cm * scale;
      const h = Math.max(3, height.cm * scale);
      const x0 = 2;
      const base = drawingHeight - 2;
      const y0 = base - h;
      const radius = Math.min(3, h / 3);

      const root = /** @type {SVGSVGElement} */ (
        svg('svg', { viewBox: `0 0 ${card} ${drawingHeight}`, width: card, height: drawingHeight, role: 'img' })
      );
      root.setAttribute('aria-label', `${piece.name}: ${one(across)} by ${one(height)}`);
      const arrow = addMarkers(root);

      // The piece: vertical rims and a softly rounded foot, with its inner wall
      root.append(
        svg('path', {
          d: `M${x0} ${y0} V${base - radius} Q${x0} ${base} ${x0 + radius} ${base} H${x0 + w - radius} Q${x0 + w} ${base} ${
            x0 + w
          } ${base - radius} V${y0}`,
          class: 'fa-dim__piece',
        })
      );
      if (h > 7) {
        const wall = Math.min(2.2, Math.max(1.2, h / 7));
        root.append(
          svg('path', {
            d: `M${x0 + wall} ${y0} V${base - wall} H${x0 + w - wall} V${y0}`,
            class: 'fa-dim__piece fa-dim__piece--inner',
          })
        );
      }

      // Guides from the rims up to the diameter line
      const lineY = 16;
      root.append(svg('line', { x1: x0, y1: lineY - 3, x2: x0, y2: y0 - 2, class: 'fa-dim__guide' }));
      root.append(svg('line', { x1: x0 + w, y1: lineY - 3, x2: x0 + w, y2: y0 - 2, class: 'fa-dim__guide' }));
      dimension(root, arrow, { x1: x0, y1: lineY, x2: x0 + w, y2: lineY, label: one(across), side: 'above' });

      // Height at the side
      const hx = x0 + w + 8;
      root.append(svg('line', { x1: x0 + w + 2, y1: y0, x2: hx + 3, y2: y0, class: 'fa-dim__guide' }));
      dimension(root, arrow, { x1: hx, y1: y0, x2: hx, y2: base, label: one(height), side: 'right' });
      root.append(svg('line', { x1: 0, y1: base + 1, x2: card, y2: base + 1, class: 'fa-dim__baseline' }));

      const figure = document.createElement('figure');
      figure.className = 'fa-dim__card';
      const acrossLine =
        piece.kind === 'oval' && piece.width
          ? bothPair(across, piece.width)
          : `Ø ${both(across)}`;
      figure.append(root, caption(title(piece), [acrossLine, `Height ${both(height)}`]));
      grid.append(figure);
    }
    return grid;
  }

  /**
   * Cork pieces seen from above: width across, length at the side.
   * @param {Piece[]} pieces
   * @param {number} width
   */
  #drawFlat(pieces, width) {
    const { columns, card } = this.#cardWidth(width, pieces.length);
    const widest = Math.max(...pieces.map((piece) => piece.width?.cm ?? 0));
    const longest = Math.max(...pieces.map((piece) => piece.length?.cm ?? 0));
    const scale = Math.min((card - 60) / widest, 90 / longest);
    const top = 26;
    const drawingHeight = Math.ceil(top + longest * scale + 4);

    const grid = document.createElement('div');
    grid.className = 'fa-dim__grid';
    grid.style.setProperty('--fa-dim-columns', String(columns));

    for (const piece of pieces) {
      const pw = /** @type {Measure} */ (piece.width);
      const pl = /** @type {Measure} */ (piece.length);
      const w = pw.cm * scale;
      const l = pl.cm * scale;
      const x0 = 2;
      // Sit every piece on the bottom edge, so all captions start right under their drawing
      const y0 = drawingHeight - 2 - l;
      const root = /** @type {SVGSVGElement} */ (
        svg('svg', { viewBox: `0 0 ${card} ${drawingHeight}`, width: card, height: drawingHeight, role: 'img' })
      );
      root.setAttribute('aria-label', `${piece.name}: ${one(pw)} by ${one(pl)}`);
      const arrow = addMarkers(root);
      root.append(svg('rect', { x: x0, y: y0, width: w, height: l, rx: Math.min(6, w / 6), class: 'fa-dim__piece' }));
      dimension(root, arrow, { x1: x0, y1: y0 - 10, x2: x0 + w, y2: y0 - 10, label: one(pw), side: 'above' });
      dimension(root, arrow, { x1: x0 + w + 8, y1: y0, x2: x0 + w + 8, y2: y0 + l, label: one(pl), side: 'right' });

      const figure = document.createElement('figure');
      figure.className = 'fa-dim__card';
      const lines = [bothPair(pw, pl)];
      // Thickness comes from the middle figure of "W x H x L" in the product data
      if (piece.height?.cm) lines.push(`Thickness ${both(piece.height)}`);
      figure.append(root, caption(title(piece), lines));
      grid.append(figure);
    }
    return grid;
  }

  /**
   * Cutlery lying flat, one piece per row, to one scale.
   * @param {Piece[]} pieces
   * @param {number} width
   */
  #drawCutlery(pieces, width) {
    const list = document.createElement('div');
    list.className = 'fa-dim__cutlery';
    const captionRoom = Math.min(170, width * 0.42);
    const trackWidth = Math.max(120, width - captionRoom - 16);
    const longest = Math.max(...pieces.map((piece) => piece.length?.cm ?? 0));
    const scale = (trackWidth - 6) / longest;
    const height = 44;

    for (const piece of pieces) {
      const length = /** @type {Measure} */ (piece.length);
      const w = length.cm * scale;
      const x0 = 3;
      const y = 32;
      const root = /** @type {SVGSVGElement} */ (
        svg('svg', { viewBox: `0 0 ${trackWidth} ${height}`, width: trackWidth, height, role: 'img' })
      );
      root.setAttribute('aria-label', `${piece.name}: ${one(length)} long`);
      const arrow = addMarkers(root);
      const head = Math.min(w * 0.3, 30);
      const kind = piece.name.toLowerCase();

      root.append(svg('path', { d: `M${x0} ${y} H${x0 + w - head}`, class: 'fa-dim__piece fa-dim__piece--thick' }));
      if (kind.includes('knife')) {
        root.append(
          svg('path', {
            d: `M${x0 + w - head} ${y - 2.5} H${x0 + w - 4} Q${x0 + w} ${y} ${x0 + w - 4} ${y + 2.5} H${x0 + w - head} Z`,
            class: 'fa-dim__piece',
          })
        );
      } else if (kind.includes('fork')) {
        const x = x0 + w - head;
        root.append(
          svg('path', {
            d: `M${x} ${y} L${x + head * 0.4} ${y - 4} H${x0 + w} M${x + head * 0.4} ${y - 1.3} H${x0 + w} M${x + head * 0.4} ${
              y + 1.3
            } H${x0 + w} M${x} ${y} L${x + head * 0.4} ${y + 4} H${x0 + w}`,
            class: 'fa-dim__piece',
          })
        );
      } else {
        root.append(svg('ellipse', { cx: x0 + w - head / 2, cy: y, rx: head / 2, ry: 4.5, class: 'fa-dim__piece' }));
      }
      root.append(svg('line', { x1: x0, y1: 13, x2: x0, y2: y - 5, class: 'fa-dim__guide' }));
      root.append(svg('line', { x1: x0 + w, y1: 13, x2: x0 + w, y2: y - 5, class: 'fa-dim__guide' }));
      dimension(root, arrow, { x1: x0, y1: 16, x2: x0 + w, y2: 16, label: one(length), side: 'above' });

      const row = document.createElement('figure');
      row.className = 'fa-dim__cutlery-row';
      row.append(root, caption(title(piece), [both(length)]));
      list.append(row);
    }
    return list;
  }
}

if (!customElements.get('fa-dimensions-sketch')) {
  customElements.define('fa-dimensions-sketch', FaDimensionsSketch);
}
